<script setup lang="ts">
import {
  useDocumentHistoryQuery,
  type DocumentHistoryQuery,
} from "@/graphql/operations";
import { computed, ref } from "vue";
import { getStorageProxyUrl, Formatter } from "@/util";
import { LoadingSpinner, BottomActionBar } from "@/components";
import { useFlashMessage } from "@/hooks";
import DocumentViewLabel from "./Label.vue";

const { displayFlashMessage } = useFlashMessage();

const offset = ref(0);
const pageSize = 50;
const {
  data,
  fetching,
  error: queryError,
  executeQuery,
} = useDocumentHistoryQuery({
  requestPolicy: "network-only",
  variables: computed(() => ({ first: pageSize, offset: offset.value })),
});

type DocumentHistoryEntry = NonNullable<
  NonNullable<DocumentHistoryQuery["documentHistoryEntries"]>["nodes"][number]
>;

interface GroupedByDay {
  day: Date;
  items: DocumentHistoryEntry[];
}

function groupByDay(
  entries: (DocumentHistoryEntry | null | undefined)[],
): GroupedByDay[] {
  const groupedByDay = entries
    .filter((e) => e && e.document != null)
    .reduce<Record<string, DocumentHistoryEntry[]>>((acc, item) => {
      if (!item) return acc;

      const day = item.date.split("T")[0];

      if (!acc[day]) {
        acc[day] = [];
      }

      acc[day].push(item);
      return acc;
    }, {});

  return Object.entries(groupedByDay).map(([day, items]) => ({
    day: new Date(day),
    items: items,
  }));
}

const entries = computed(() => {
  const d = data.value?.documentHistoryEntries?.nodes;
  if (!d) {
    return;
  }

  const result = groupByDay(d);
  return result;
});

const selectedViews = ref<Array<string>>([]);
const displayBottomActionBar = computed(() => selectedViews.value.length > 0);

const toggleViewSelection = (id: string) => {
  if (selectedViews.value.includes(id)) {
    selectedViews.value.splice(selectedViews.value.indexOf(id), 1);
  } else {
    selectedViews.value.push(id);
  }

  checkGroupCheckboxStates();
};

const toggleGroupSelection = (items: DocumentHistoryEntry[]) => {
  const itemIds = items.map((i) => i.id);
  if (groupIsSelected(items)) {
    selectedViews.value = selectedViews.value.filter(
      (id) => !itemIds.includes(id),
    );
  } else {
    selectedViews.value = [...new Set([...selectedViews.value, ...itemIds])];
  }

  checkGroupCheckboxStates();
};

const groupIsSelected = (items: DocumentHistoryEntry[]) => {
  return items.every((i) => selectedViews.value.includes(i.id));
};

const resetSelectedViews = () => {
  selectedViews.value = [];
  checkGroupCheckboxStates();
};

const checkboxRefs = ref();
const checkGroupCheckboxStates = () => {
  if (!entries.value || !checkboxRefs.value) {
    return;
  }

  for (let i = 0; i < entries.value.length; i++) {
    if (groupIsIndeterminate(entries.value[i].items)) {
      checkboxRefs.value[i].indeterminate = true;
    } else {
      checkboxRefs.value[i].indeterminate = false;
    }
  }
};

const groupIsIndeterminate = (items: DocumentHistoryEntry[]) => {
  const selectedInGroup = items.filter((i) =>
    selectedViews.value.includes(i.id),
  );
  return selectedInGroup.length > 0 && selectedInGroup.length < items.length;
};

const handleSuccess = () => {
  resetSelectedViews();
  reload();
  displayFlashMessage("Success", "success");
};

const selectedAssignments = computed(() =>
  (data.value?.documentHistoryEntries?.nodes ?? []).filter(
    (entry): entry is DocumentHistoryEntry =>
      Boolean(entry && selectedViews.value.includes(entry.id)),
  ),
);
const reload = () => executeQuery({ requestPolicy: "network-only" });
function changePage(direction: number) {
  resetSelectedViews();
  offset.value = Math.max(0, offset.value + direction * pageSize);
}
</script>

<template>
  <div class="pane page_content" data-test="classic-presentation-history">
    <div class="title_container">
      <h3>{{ $t("documentHistory.title") }}</h3>
    </div>

    <div v-if="queryError" role="alert">
      {{ $t("documentHistory.hcps.reloadFailed") }}
      <button type="button" class="button secondary" @click="reload">
        {{ $t("documentHistory.hcps.reload") }}
      </button>
    </div>
    <div
      v-if="fetching && !data?.documentHistoryEntries"
      class="loading_status"
    >
      <LoadingSpinner :color="'var(--classic-text, black)'" />
    </div>

    <div v-else-if="!queryError && (!entries || entries.length === 0)">
      <div>{{ $t("documentHistory.empty") }}</div>
    </div>

    <div v-else-if="entries">
      <div v-for="(group, idx) in entries" :key="idx" class="section">
        <div class="section_title_container">
          <div class="section_title">
            {{ Formatter.translateDate(group.day.toISOString()) }}
          </div>
          <input
            type="checkbox"
            class="right"
            @change="toggleGroupSelection(group.items)"
            :checked="groupIsSelected(group.items)"
            ref="checkboxRefs"
          />
        </div>

        <div v-for="entry in group.items" :key="entry.id" class="row">
          <template v-if="entry!.document">
            <div>
              <div class="container">
                <div class="thumbnail_container">
                  <img
                    class="thumbnail_image"
                    :src="
                      getStorageProxyUrl(entry!.document.thumbnailDownloadUrl)
                    "
                  />
                </div>
                <div>
                  <div class="doc_title">
                    {{ entry!.document.displayTitle }}
                  </div>
                  <div class="additional_info">
                    {{ Formatter.formatAndTranslateDate(entry!.startsAt) }}
                    &mdash;
                    {{
                      Formatter.formatDuration({
                        hours: entry!.activeDuration?.hours || 0,
                        minutes: entry!.activeDuration?.minutes || 0,
                        seconds: entry!.activeDuration?.seconds || 0,
                      })
                    }}
                  </div>
                </div>
              </div>
            </div>
            <div class="right">
              <DocumentViewLabel
                :viewIds="[entry.id]"
                :label="entry.label"
                :assignments="[entry]"
                @success="handleSuccess"
                @reload="reload"
              />
              <input
                type="checkbox"
                :checked="selectedViews.includes(entry.id)"
                @change="toggleViewSelection(entry.id)"
              />
            </div>
          </template>
        </div>
      </div>
    </div>

    <div
      v-if="!fetching && data?.documentHistoryEntries"
      class="history_pagination"
    >
      <button
        type="button"
        class="button secondary"
        :disabled="offset === 0"
        data-test="history-previous"
        @click="changePage(-1)"
      >
        {{ $t("crmContacts.pagination.previous") }}
      </button>
      <button
        type="button"
        class="button secondary"
        :disabled="!data.documentHistoryEntries.pageInfo.hasNextPage"
        data-test="history-next"
        @click="changePage(1)"
      >
        {{ $t("crmContacts.pagination.next") }}
      </button>
    </div>
    <BottomActionBar v-if="displayBottomActionBar" @close="resetSelectedViews">
      <template #action>
        <span class="label">
          {{
            $t("documentHistory.multiselect.label", {
              count: selectedViews.length,
            })
          }}
        </span>
        <DocumentViewLabel
          :viewIds="selectedViews"
          :assignments="selectedAssignments"
          :singleButton="true"
          :type="'presentation'"
          @success="handleSuccess"
          @reload="reload"
        />
        <DocumentViewLabel
          :viewIds="selectedViews"
          :assignments="selectedAssignments"
          :singleButton="true"
          :type="'self-study'"
          @success="handleSuccess"
          @reload="reload"
        />
      </template>
    </BottomActionBar>
  </div>
</template>

<style lang="scss" scoped>
.history_pagination {
  display: flex;
  justify-content: flex-end;
  gap: 0.5em;
  margin-bottom: 5em;
}

.pane {
  font-size: 14px;
}

.section {
  margin-bottom: 1em;
}

.section_title_container {
  padding: 1em 1em 1em 0;
  display: flex;
  align-items: center;
}

.section_title {
  color: var(--classic-text-muted, #737373);
  font-weight: 500;
}

.section_title_container .right {
  margin-left: auto;
}

input[type="checkbox"] {
  height: 16px;
  width: 16px;
  padding: 0.5em;
}

@media (prefers-color-scheme: dark) {
  :global(
    .classic-theme
      [data-test="classic-presentation-history"]
      input[type="checkbox"]:focus-visible
  ) {
    outline: 2px solid var(--classic-focus);
    outline-offset: 2px;
  }
}

.row {
  border-top: 1px solid var(--classic-border-subtle, #ececec);
  padding: 1em 1em 1em 0;
  display: flex;
}

.container {
  display: flex;
  align-items: center;
}

.thumbnail_container {
  height: 60px;
  width: 80px;
  margin-right: 1em;
}

.thumbnail_image {
  height: 60px;
  width: 80px;
  object-fit: contain;
  border-radius: 4%;
  background-color: var(--classic-media-surface, #fafafa);
}

.additional_info {
  color: var(--classic-text-muted, #737373);
  font-size: 14px;
  line-height: 1.5;
}

.doc_title {
  color: var(--classic-text-strong, #000000);
  font-weight: 500;
  line-height: 1.5;
}

.row .right {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.document_view_label {
  margin-right: 1em;
}

span.label {
  margin-right: 1em;
}
</style>
